import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { storiesAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { 
  Check, FileText, Target, MessageSquare, Download, Shield, 
  ChevronRight, Star, Briefcase, MapPin, Clock, Quote, Award,
  Menu, X
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage, languages } = useLanguage();
  const [stories, setStories] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const response = await storiesAPI.getAll();
      setStories(response.data.stories || []);
    } catch (error) {
      console.error('Failed to load stories');
    }
  };

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: t('landing.features.ats.title'),
      description: t('landing.features.ats.desc')
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: t('landing.features.match.title'),
      description: t('landing.features.match.desc')
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: t('landing.features.interview.title'),
      description: t('landing.features.interview.desc')
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: t('landing.features.pdf.title'),
      description: t('landing.features.pdf.desc')
    }
  ];

  const cvTypes = [
    { name: t('landing.cvTypes.noExp.title'), desc: t('landing.cvTypes.noExp.desc') },
    { name: t('landing.cvTypes.entry.title'), desc: t('landing.cvTypes.entry.desc') },
    { name: t('landing.cvTypes.exp.title'), desc: t('landing.cvTypes.exp.desc') },
    { name: t('landing.cvTypes.switch.title'), desc: t('landing.cvTypes.switch.desc') }
  ];

  const getCVTypeColor = (type) => {
    switch (type) {
      case 'No Experience': return 'bg-amber-100 text-amber-700';
      case 'Entry Level': return 'bg-blue-100 text-blue-700';
      case 'Career Switcher': return 'bg-purple-100 text-purple-700';
      case 'Experienced': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src="/logo-nav.png" alt="Career Step" className="h-10 w-auto" />
              <span className="font-bold text-lg sm:text-2xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Career Step</span>
            </Link>
            {/* Desktop nav */}
            <div className="hidden sm:flex items-center gap-3">
              <LanguageSelector variant="compact" />
              <Link to="/about">
                <Button variant="ghost" data-testid="nav-about-btn" className="text-slate-600 hover:text-slate-900">
                  {t('nav.about')}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-btn" className="text-slate-600 hover:text-slate-900">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="nav-register-btn" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold">
                  {t('nav.getStarted')}
                </Button>
              </Link>
            </div>
            {/* Mobile hamburger */}
            <button
              data-testid="mobile-menu-toggle"
              className="sm:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white border-t border-slate-200 shadow-lg animate-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-3 space-y-1">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block">
                <div data-testid="mobile-nav-about" className="flex items-center gap-3 px-3 py-3 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors font-medium">
                  {t('nav.about')}
                </div>
              </Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block">
                <div data-testid="mobile-nav-login" className="flex items-center gap-3 px-3 py-3 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors font-medium">
                  {t('nav.login')}
                </div>
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block">
                <div data-testid="mobile-nav-register" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-center justify-center hover:bg-amber-600 transition-colors">
                  {t('nav.getStarted')}
                </div>
              </Link>
              <div className="border-t border-slate-100 pt-2 mt-1">
                <p className="px-3 py-1 text-xs text-slate-400 font-medium uppercase tracking-wider">Language</p>
                <div className="flex flex-wrap gap-2 px-3 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      data-testid={`mobile-lang-${lang.code}`}
                      onClick={() => setLanguage(lang.code)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        language === lang.code
                          ? 'bg-amber-100 text-amber-700 border border-amber-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 lg:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 min-h-[60vh] lg:min-h-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full text-sm font-medium mb-4 lg:mb-6">
                <Star className="w-4 h-4" />
                {t('landing.badge')}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 lg:mb-6" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '1.1' }}>
                {t('landing.title')}{' '}
                <span className="text-amber-500">{t('landing.titleHighlight')}</span>
              </h1>
              <p className="text-base lg:text-lg text-slate-300 mb-6 lg:mb-8 max-w-xl mx-auto lg:mx-0">
                {t('landing.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Button 
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/register')}
                  className="h-12 lg:h-14 px-6 lg:px-8 bg-amber-500 hover:bg-amber-600 text-white text-base lg:text-lg font-bold rounded-xl shadow-lg shadow-amber-500/30"
                >
                  {t('landing.cta')}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            {/* Pricing Card in Hero */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl border-2 border-amber-500 p-8 shadow-2xl max-w-sm mx-auto relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-amber-500 text-white px-4 py-1 text-sm font-bold rounded-bl-xl">
                  {t('landing.pricing.bestValue', 'BEST VALUE')}
                </div>
                <div className="text-center mb-6">
                  <p className="text-slate-600 mb-2">{t('landing.pricing.title')}</p>
                  <span className="text-5xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('landing.pricing.price')}</span>
                  <span className="text-slate-600 text-sm ml-1">{t('landing.pricing.period')}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {[
                    t('landing.pricing.features.cvs'),
                    t('landing.pricing.features.ai'),
                    t('landing.pricing.features.interview'),
                    t('landing.pricing.features.matching'),
                    t('landing.pricing.features.pdf')
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-700 text-sm">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl"
                >
                  {t('landing.pricing.cta')}
                </Button>
                <p className="text-slate-500 text-xs mt-3 text-center">
                  {t('landing.pricing.note')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Message */}
      <section className="py-6 bg-emerald-50 border-y border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-emerald-800 font-medium flex items-center justify-center gap-2 flex-wrap">
            <Shield className="w-5 h-5" />
            <span className="font-bold">{t('landing.promise')}</span> {t('landing.promiseText')}
          </p>
        </div>
      </section>

      {/* SUCCESS STORIES - Now on Landing Page */}
      <section id="success-stories" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4" />
              {t('landing.successStories.badge')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('landing.successStories.title')}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {t('landing.successStories.subtitle')}
            </p>
          </div>

          {/* Stories Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.slice(0, 6).map((story) => (
              <div 
                key={story.id}
                className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-amber-500/50 transition-all"
              >
                {/* Header with initials */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-white">{story.initials}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{story.name}</h3>
                    <p className="text-slate-400 text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {story.location}
                    </p>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCVTypeColor(story.cv_type)}`}>
                    {story.cv_type}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-900/50 text-emerald-400 font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {story.time_to_job}
                  </span>
                </div>

                {/* Before/After */}
                <div className="space-y-3 mb-4">
                  <div className="bg-red-900/30 rounded-lg p-3 border border-red-800/50">
                    <p className="text-xs font-semibold text-red-400 mb-1">{t('landing.successStories.before')}</p>
                    <p className="text-sm text-red-200">{story.before}</p>
                  </div>
                  <div className="bg-emerald-900/30 rounded-lg p-3 border border-emerald-800/50">
                    <p className="text-xs font-semibold text-emerald-400 mb-1">{t('landing.successStories.after')}</p>
                    <p className="text-sm text-emerald-200">{story.after}</p>
                  </div>
                </div>

                {/* Quote */}
                <div className="flex items-start gap-2 text-slate-300">
                  <Quote className="w-4 h-4 flex-shrink-0 mt-1 text-amber-500" />
                  <p className="text-sm italic">{story.quote}</p>
                </div>

                {/* Key Feature */}
                <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-xs text-slate-500">{t('landing.successStories.keyFeature')}</span>
                  <span className="text-xs font-semibold text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    {story.key_feature}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* CTA after stories */}
          <div className="mt-12 text-center">
            <p className="text-slate-400 mb-4">{t('landing.successStories.cta')}</p>
            <Button 
              onClick={() => navigate('/register')}
              className="h-14 px-8 bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold rounded-xl shadow-lg shadow-amber-500/30"
            >
              {t('landing.successStories.button')}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Motivational Hook */}
      <section className="py-8 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xl sm:text-2xl text-white font-medium">
            {t('landing.motivation.text1')} <span className="text-amber-400 font-bold">R25</span> {t('landing.motivation.text2')}
          </p>
          <p className="text-slate-400 mt-2">
            {t('landing.motivation.subtext')}
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 bg-white rounded-xl border border-slate-200 hover:border-amber-500/50 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4 text-white">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ATS-Safe Promise */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-emerald-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              {t('landing.ats.badge')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('landing.ats.title')}
            </h2>
            <p className="text-lg text-emerald-100 max-w-2xl mx-auto">
              {t('landing.ats.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: t('landing.ats.feature1.title'),
                description: t('landing.ats.feature1.desc')
              },
              {
                title: t('landing.ats.feature2.title'),
                description: t('landing.ats.feature2.desc')
              },
              {
                title: t('landing.ats.feature3.title'),
                description: t('landing.ats.feature3.desc')
              },
              {
                title: t('landing.ats.feature4.title'),
                description: t('landing.ats.feature4.desc')
              },
              {
                title: t('landing.ats.feature5.title'),
                description: t('landing.ats.feature5.desc')
              },
              {
                title: t('landing.ats.feature6.title'),
                description: t('landing.ats.feature6.desc')
              }
            ].map((item, index) => (
              <div key={index} className="bg-emerald-800/50 rounded-xl p-5 border border-emerald-700/50">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-emerald-200 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <p className="text-emerald-300 text-sm">
              {t('landing.ats.tip')}
            </p>
          </div>
        </div>
      </section>

      {/* CV Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('landing.cvTypes.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('landing.cvTypes.subtitle')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cvTypes.map((type, index) => (
              <div 
                key={index}
                className="p-6 bg-white rounded-xl border border-slate-200 hover:border-amber-500 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <Briefcase className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">{type.name}</h3>
                <p className="text-slate-600 text-sm">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Translator */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                {t('landing.translator.badge')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('landing.translator.title')}
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                {t('landing.translator.subtitle')}
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-slate-100 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">{t('landing.translator.input')}</p>
                  <p className="text-slate-900">"{t('landing.translator.inputExample')}"</p>
                </div>
                <div className="flex justify-center">
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-5 h-5 text-white rotate-90" />
                  </div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="text-sm text-emerald-600 mb-1">{t('landing.translator.output')}</p>
                  <p className="text-slate-900">"{t('landing.translator.outputExample')}"</p>
                </div>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="bg-amber-50 rounded-2xl p-8 border border-amber-200 max-w-sm">
                <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center">{t('landing.translator.cta.title')}</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500" />
                    {t('landing.translator.cta.1')}
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500" />
                    {t('landing.translator.cta.2')}
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500" />
                    {t('landing.translator.cta.3')}
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl"
                >
                  {t('landing.translator.cta.button')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - More Prominent Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('landing.finalCta.title')}
          </h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            {t('landing.finalCta.subtitle')}
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md mx-auto mb-8">
            <div className="mb-6">
              <span className="text-5xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('landing.finalCta.price')}</span>
              <p className="text-slate-600 mt-1">{t('landing.finalCta.period')}</p>
              <p className="text-emerald-600 text-sm font-medium mt-2">{t('landing.finalCta.note')}</p>
            </div>
            <ul className="text-left space-y-3 mb-6">
              {[
                t('landing.finalCta.feature1'),
                t('landing.finalCta.feature2'),
                t('landing.finalCta.feature3'),
                t('landing.finalCta.feature4'),
                t('landing.finalCta.feature5'),
                t('landing.finalCta.feature6'),
                t('landing.finalCta.feature7')
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-700">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Button 
              data-testid="final-cta-btn"
              onClick={() => navigate('/register')}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white text-lg font-bold rounded-xl"
            >
              {t('landing.finalCta.button')}
            </Button>
          </div>
          
          <p className="text-amber-100 text-sm">
            <Shield className="w-4 h-4 inline mr-1" />
            {t('landing.finalCta.secure')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-900" />
                </div>
                <span className="font-bold text-xl">{t('landing.footer.brand')}</span>
              </div>
              <p className="text-slate-400 text-sm">
                {t('landing.footer.brandDesc')}
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.quickLinks')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/register" className="hover:text-white transition-colors">{t('landing.footer.getStarted')}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('landing.footer.login')}</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">{t('landing.footer.about')}</Link></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</Link></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.connect')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <a href="https://www.facebook.com/share/1CG6LxFxa6/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    {t('landing.footer.facebook')}
                  </a>
                </li>
                <li>
                  <a href="mailto:support@careerstep.co.za" className="hover:text-white transition-colors">
                    support@careerstep.co.za
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Career Step. {t('landing.footer.copyright')}
            </p>
            <p className="text-slate-500 text-sm">
              {t('landing.footer.promise')}
            </p>
          </div>
          <p className="text-slate-600 text-xs text-center mt-4">
            Career Step is a CV creation tool. We do not guarantee employment, interviews, or job offers. Results depend on individual effort, qualifications, and market conditions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
